import { Component, JSX } from 'react';
import { toReadableBytes } from '../../../utils/file';
import { previewKindFor, PreviewKind } from '../../../utils/preview-kind';
import { chainToTool } from '../../../services/chain-to-tool.service';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { container } from '../../../context/module';
import { MetricsService } from '../../../services/metrics.service';
import { MetricEventName } from '../../../types/metric-events';
import { suggestionsFor } from '../../../utils/next-tool';
import { canPreviewOutput, CapabilityResult } from '../../../utils/media-capability';

import { lazyInject } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { Logger } from '../../../models/logger';
import { GAService } from '../../../services/analytics.service';
import { EnvironmentService } from '../../../services/environment.service';
import { EnforcementErrorService } from '../../../services/enforcement-error.service';
import { FileDownloadService } from '../../../services/file-download.service';
import { FileManagerService } from '../../../services/file-manager.service';
import { FileProcessService } from '../../../services/file-process.service';
import { ProcessCompleteCheckService } from '../../../services/process-complete-check.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SpinnerService } from '../../../services/spinner.service';
import { UIContext } from '../../../services/ui-context';
import { GAContants } from '../../../types/ga.types';
import { OptionalChildrenProps } from '../../../types/react.types';
import { RouteProps } from '../../../types/route-types';
import {
    ProcessStageSubStateTypes,
    ProcessStageTypes,
    ProcessTypes,
    StatusTypes,
    WebsocketProcessTypes,
    WebsocketResponse,
} from '../../../types/types';
import { ConvertOptionsUtils } from '../../../utils/convert-options-utils';
import { ConvertOptionsV2Utils } from '../../../utils/convert-options-v2-utils';
import { PSDate, PSRedableDuration } from '../../../utils/date.utils';
import { getExtension, replaceExtension } from '../../../utils/file';
import { get, isNaN, isNil, isNumber, isString, toInteger } from '../../../utils/lodash-min';
import { SafeExecUtils } from '../../../utils/safe-exec.utils';
import { StringUtils } from '../../../utils/string-utils';
import { FileUploadReEditModalComponent } from '../file-upload-reedit/file-upload-reedit-modal.component';
import { MediaPreviewModalComponent } from '../media-preview/media-preview-modal.component';
import Modal from '../modal/modal.component';
import ProgressComponent from '../progress/progress.component';
import { RippleLoaderComponent } from '../ripple-loader/ripple-loader.component';
import { SingleFileDownloadModalComponent } from '../single-fle-download/single-file-download.component';
import Tooltip from '../tooltip/tooltip.component';
import styles from './file-list-item.component.module.scss';

declare var gapi;
declare var Dropbox;

interface FileListItemComponentProps extends RouteProps, OptionalChildrenProps {
    statusCheckButtonEnabled?: boolean;
    reeditButtonEnabled?: boolean;
    deleteButtonEnabled?: boolean;
    fileUpload?: FileUpload;
    hideFooterButtons?: boolean;
}
interface FileListItemComponentState {
    zipDownloadInProgress: boolean;
    linkCopied: boolean;
}

export class FileListItemComponent extends Component<FileListItemComponentProps, FileListItemComponentState> {
    private readonly logger = Logger.getLogger('FileListItemComponent');
    readonly FILEUPLOAD_STATE_FAILED = StatusTypes.FAILED;

    fileProcessInfo: string;
    fileOutputInfo: string | JSX.Element;


    get fileUpload() {
        return this.props.fileUpload;
    }

    get hasZipFileInfo() {
        return this.props.fileUpload && this.props.fileUpload.zipResult;
    }

    get zipSupported() {
        return this.fileUpload && (this.fileUpload.processType === ProcessTypes.SPLIT ||
            (this.fileUpload.ws && this.fileUpload.ws.processType === WebsocketProcessTypes.CONVERT_ONE_TO_MANY));
    }

    get canRequestZipFile() {
        return this.zipSupported && !this.fileUpload.zipResult;
    }

    get isZipFileReady() {
        return this.hasZipFileInfo && this.fileUpload.zipResult.processStageSubState === ProcessStageSubStateTypes.COMPLETED;
    }

    get hasZipFileData() {
        if (!this.hasZipFileInfo) {
            return 'No data';
        }
        if (this.fileUpload.zipResult.processStageSubState === ProcessStageSubStateTypes.COMPLETED) {
            return 'File zipped successfully';
        }
        return this.fileUpload.zipResult.data;
    }

    get statusCheckButtonEnabled() {
        return isNil(this.props.statusCheckButtonEnabled) || this.props.statusCheckButtonEnabled;
    }
    get reeditButtonEnabled() {
        return isNil(this.props.reeditButtonEnabled) || this.props.reeditButtonEnabled;
    }
    get deleteButtonEnabled() {
        return isNil(this.props.deleteButtonEnabled) || this.props.deleteButtonEnabled;
    }

    get hasMultipleFilesToDownload(): boolean {
        return this.fileUpload && this.fileUpload.result && Array.isArray(this.fileUpload.result.output);
    }

    @lazyInject(FileDownloadService) private fileDownloadService: FileDownloadService;
    @lazyInject(GAService) private gaService: GAService;
    @lazyInject(UIContext) private uiContext: UIContext;
    @lazyInject(ProcessCompleteCheckService) private processCompleteCheckService: ProcessCompleteCheckService;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(SpinnerService) private spinner: SpinnerService;
    @lazyInject(FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(FileProcessService) private fileProcessService: FileProcessService;
    @lazyInject(EnforcementErrorService) private enforcementErrorService: EnforcementErrorService;
    @lazyInject(EnvironmentService) private envService: EnvironmentService;

    readonly state: Readonly<FileListItemComponentState> = { zipDownloadInProgress: false, linkCopied: false };

    /**
     * Suggestions that need a browser-decodable image, once the runtime check has answered.
     * Until then they are withheld: offering "Resize Image" for a TIFF sends the user to a
     * canvas editor that cannot open it and silently shows its landing page instead.
     */
    private editorSafe: boolean | null = null;

    /** Guards the async capability probe from updating a card the user already deleted. */
    private mounted = false;

    /** Slug being chained from THIS card, so only its own chip shows progress. */
    private chaining: string | null = null;
    /** Why the file did not travel, shown on this card rather than as a page-level toast. */
    private chainNotice: string | null = null;

    /**
     * Clear the busy flag when the page comes back.
     *
     * Chaining navigates away, so nothing ever cleared `chaining` on the success path -- and a
     * browser Back restores this page from the bfcache without remounting, so the flag survived.
     * The chip stayed on "Taking your file…" and `if (this.chaining) return` then swallowed
     * every later click on every chip. Reported as "none of those pills are clickable".
     */
    private resetChainState = () => {
        if (!this.mounted) return;
        if (this.chaining || this.chainNotice) {
            this.chaining = null;
            this.chainNotice = null;
            this.forceUpdate();
        }
    };

    getZipFileURL(fileInfo: FileUpload) {
        if (!fileInfo.result) {
        } else if (fileInfo.result.error === true) {
        } else {
            try {
                const tokens = fileInfo.result.zip.split('\\');
                return this.fileDownloadService.getDownloadURL(fileInfo, tokens[tokens.length - 1]);
            } catch (e) {
                return;
            }
        }
    }

    componentDidMount() {
        this.mounted = true;
        const toType = this.props.fileUpload?.toType;
        if (toType) {
            canPreviewOutput(`${toType}`)
                .then(verdict => {
                    // 'unknown' counts as safe: the editor is the one that will find out, and
                    // withholding a useful destination on a maybe is the worse error.
                    this.applyEditorSafe(verdict !== 'no');
                })
                .catch(() => this.applyEditorSafe(true));
        }
        if (BrowserUtils.hasWindow()) {
            // `pageshow` fires on a bfcache restore, where componentDidMount does not run again.
            window.addEventListener('pageshow', this.resetChainState);
            window.addEventListener('visibilitychange', this.resetChainState);
        }
    }

    /** The capability probe resolves off-thread; a card removed before then must not update. */
    private applyEditorSafe(safe: boolean) {
        if (!this.mounted || safe === this.editorSafe) return;
        this.editorSafe = safe;
        this.forceUpdate();
    }

    componentWillUnmount() {
        this.mounted = false;
        if (BrowserUtils.hasWindow()) {
            window.removeEventListener('pageshow', this.resetChainState);
            window.removeEventListener('visibilitychange', this.resetChainState);
        }
    }

    async reedit(fileUpload: FileUpload) {
        await this.initializeScriptsAndWebsockets();
        const initialState = {
            fileUpload,
        };
        Modal.createModal(FileUploadReEditModalComponent, initialState);
    }

    private async initializeScriptsAndWebsockets() {
        await this.spinner.runWithLoader(async () => {
            try {
                await this.scriptLoadService.loadAllScriptsIfNotLoaded();
            } catch (e) {
                this.logger.error({ message: 'Something went wrong while loading scripts.', error: e });
            }
        }, 'Please wait while we initialize file upload service...');
    }

    hasDirectDownloadLink(fileUpload: FileUpload): boolean {
        if (!this.isFileReady(fileUpload) || fileUpload.result.error === true) {
            return false;
        }

        if (Array.isArray(fileUpload.result.output)) {
            return false;
        }
        return true;
    }

    getRangeSupportedDirectDownloadLink(fileUpload: FileUpload, preview = false): string {
        if (!this.hasDirectDownloadLink(fileUpload)) {
            return null;
        }
        if (Array.isArray(fileUpload.result.output)) {
            return null;
        }

        const tokens = fileUpload.result.output.split('\\');
        const link = this.fileDownloadService.getRangeSupportedDirectDownloadLink(fileUpload, tokens[tokens.length - 1], preview);
        setTimeout(() => {
            gapi.savetodrive.go(`id${this.props.fileUpload.id}`);
        }, 100);
        return link;
    }

    async onDirectDownloadBtnHandler(fileInfo: FileUpload) {
        await this.scriptLoadService.loadAllScriptsIfNotLoaded();
        if (!this.isFileReady(fileInfo)) {
            this.uiContext.warnToast('Processing File..', 'We are processing your file. Please wait...', true, true);
        } else if (fileInfo.result.error === true) {
            this.uiContext.errorToast(
                'Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`
                , true,
                true);
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                const initialState = {
                    title: 'Download individual files',
                    files: fileInfo.result.output,
                    name: fileInfo.name,
                    numberOfFiles: fileInfo.result.output.length,
                    class: 'gray modal-lg',
                    fileInfo: fileInfo,
                };
                Modal.createModal(SingleFileDownloadModalComponent, initialState);
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory.BUTTON,
                    GAContants.EventAction['MODEL-OPEN'],
                    GAContants.EventLabel.NAVIGATE,
                    fileInfo.name,
                );
            } else {
                const tokens = fileInfo.result.output.split('\\');
                const link = this.fileDownloadService.getDirectDownloadLink(fileInfo, tokens[tokens.length - 1]);
                this.uiContext.showSuccessDialog("Download Links", null, `
                    <div style="text-align: left; word-break: break-all;">
                        <div>
                            Filename: 
                            <div class="alert alert-info">${fileInfo.name}</div>
                        </div>
                        <div>
                            Link:
                            <div class="alert alert-info" style="">${link}</div>
                        </div>
                        <div>*To download file directly, copy and paste above link to your browser's address bar.</div>
                    </div>
                `);
            }
        }
    }


    onDownloadBtnHandler(fileInfo: FileUpload) {
        if (!this.isFileReady(fileInfo)) {
            this.uiContext.warnToast('Processing File..', 'We are processing your file. Please wait...', true, true);
        } else if (fileInfo.result.error === true) {
            this.uiContext.errorToast(
                'Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`
                , true,
                true);
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                const initialState = {
                    title: 'Download individual files',
                    files: fileInfo.result.output,
                    name: fileInfo.name,
                    numberOfFiles: fileInfo.result.output.length,
                    class: 'gray modal-lg',
                    fileInfo: fileInfo,
                };
                Modal.createModal(SingleFileDownloadModalComponent, initialState);
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory.BUTTON,
                    GAContants.EventAction['MODEL-OPEN'],
                    GAContants.EventLabel.NAVIGATE,
                    fileInfo.name,
                );
            } else {
                const tokens = fileInfo.result.output.split('\\');
                this.fileDownloadService.downloadFile(fileInfo, tokens[tokens.length - 1]);
            }
        }
    }

    async onZipDownloadBtnHandler(fileInfo: FileUpload) {
        if (!fileInfo.result) {
            this.uiContext.warnToast('Processing File',
                'We are processing your file. Please wait...', false, true);
        } else if (fileInfo.result.error === true) {
            this.uiContext.errorToast('Error',
                `There was an error converting your file. Please let us know at ${this.envService.env.siteSupportEmail}`, true, true);
        } else {
            await this.initializeScriptsAndWebsockets();
            if (this.isZipFileReady) {
                this.openZippedFileDownloadWindow(fileInfo);
            } else if (!this.canRequestZipFile) {
                this.uiContext.warnToast('Processing File',
                    'We are processing your file. Please wait...', false, true);
                return;
            } else {
                this.spinner.runWithLoader(async () => {
                    this.setState({ zipDownloadInProgress: true });
                    await this.fileProcessService.createZip(fileInfo).then(() => {
                        this.logger.trace1('success');
                        this.openZippedFileDownloadWindow(fileInfo);
                        this.setState({ zipDownloadInProgress: false });
                    }).catch((err) => {
                        this.logger.error(err);
                        this.setState({ zipDownloadInProgress: false });
                    });
                    console.log('Zip completed');
                }, 'Zipping files for downloading..');
            }

        }
    }

    private openZippedFileDownloadWindow(fileInfo) {
        const tokens = fileInfo.result.zip.split('\\');
        const downloadWindowOpened = this.fileDownloadService.downloadFile(fileInfo, tokens[tokens.length - 1]);
        if (!downloadWindowOpened) {
            this.uiContext.warnToast('Pop-up blocked',
                'If file upload failed. Please make sure your browser not blocking pop-ups...', false, true);
        }
        return;
    }

    getDownloadURL(fileInfo: FileUpload) {
        if (!this.isFileReady(fileInfo)) {
        } else if (fileInfo.result.error === true) {
        } else {
            if (Array.isArray(fileInfo.result.output)) {
                return 'modal';
            } else {
                try {
                    const tokens = fileInfo.result.output.split('\\');
                    return this.fileDownloadService.getDownloadURL(fileInfo, tokens[tokens.length - 1]);
                } catch (e) {
                    this.logger.error({ message: `Failed to decode result.output`, error: e, data: FileUpload.toLoggableObject(fileInfo) });
                    return;
                }
            }
        }
    }

    getOutputInfo(fileUpload: FileUpload): JSX.Element | string {
        if (!this.fileOutputInfo) {
            this.fileOutputInfo = this._getOutputInfo(fileUpload);
        }
        return this.fileOutputInfo;
    }

    private _getOutputInfo(fileUpload: FileUpload) {
        if (fileUpload.processType === ProcessTypes.COMPRESS) {
            const key = Object.keys(fileUpload.result.sizes.outputs);
            const outputSize = parseFloat(fileUpload.result.sizes.outputs[key[0]]);
            const ratio = parseFloat(1 * (100 * (1 - outputSize / fileUpload.size)) + '').toFixed(2);
            const ratio2 = parseFloat(1 * (100 * (outputSize / fileUpload.size)) + '').toFixed(2);

            return <div>
                {/* <div>Original size: {this.getFileSize(fileUpload.size)}</div> */}
                <div>New size:  {this.getFileSizeInfo(fileUpload)} ({ratio2}% of the original {this.getFileSize(fileUpload.size)})</div>
            </div>;
        }
        return `Output Size: ${this.getFileSizeInfo(fileUpload)}`;
    }

    private getFileSizeInfo(fileUpload: FileUpload) {
        if (fileUpload.processType !== ProcessTypes.SPLIT) {
            try {
                const key = Object.keys(fileUpload.result.sizes.outputs);
                return this.getFileSize(fileUpload.result.sizes.outputs[key[0]]);
            } catch (e) {
                this.logger.error({ message: e.message, error: e });
            }
        }
        return '';
    }

    private getFileSize(size) {
        // Decimal (SI), matching the tool-page file card and the backend's size targets.
        // This used to be filesize(size, { base: 2, standard: 'jedec' }) -- binary maths
        // wearing decimal labels -- so one 590,213-byte file read "590.21 KB" on the tool
        // page and "576.38 KB" here. On a site whose whole job is file size, the number
        // changing between screens reads as a bug in the conversion.
        return toReadableBytes(size, 'quantity');
    }

    isSingleFile(outputs: any) {
        return Object.keys(outputs).length === 1;
    }

    decodeDataType(fileUpload: FileUpload, dataType: string, data: string) {
        const wsData: WebsocketResponse = fileUpload.ws;
        if (dataType === 'enforcement_error') {
            let parsed: Record<string, unknown> = {};
            try {
                parsed = JSON.parse(data);
            } catch { /* fall through to fallback copy */ }
            // Surface in the global modal as well so the user gets the same
            // structured rejection UI regardless of which code path (pre-upload
            // resolve 429 vs post-upload WS) triggered the block.
            try {
                this.enforcementErrorService?.show(
                    EnforcementErrorService.fromBody(parsed as never, 429),
                );
            } catch { /* best-effort — never block the inline message */ }
            return (parsed as { userMessage?: string }).userMessage
                || 'Usage limit reached. Please try again later.';
        }
        if (dataType === 'notification') {
            if (wsData.processStage == ProcessStageTypes.UPLOAD) {
                if (wsData.processStageSubState == ProcessStageSubStateTypes.COMPLETED) {
                    const totalFileSize = SafeExecUtils.exec(() => fileUpload.originalInputFiles.map(f => f.metadata.fsize).reduce((p, c) => p + c, 0), null, -1);
                    const dataVal = toInteger(data);
                    if (totalFileSize !== -1 && isNumber(totalFileSize) && !isNaN(totalFileSize) && isNumber(dataVal) && !isNaN(dataVal)) {
                        return `Sending file to worker ${this.getFileSize(dataVal)}/${this.getFileSize(totalFileSize)}`;
                    }
                    return 'File sent to worker';
                } else if (wsData.processStageSubState == ProcessStageSubStateTypes.PARTIAL) {
                    const totalFileSize = SafeExecUtils.exec(() => fileUpload.originalInputFiles.map(f => f.metadata.fsize).reduce((p, c) => p + c, 0), null, -1);
                    const dataVal = toInteger(data);
                    if (totalFileSize !== -1 && isNumber(totalFileSize) && !isNaN(totalFileSize) && isNumber(dataVal) && !isNaN(dataVal)) {
                        return `Sending file to worker ${this.getFileSize(dataVal)}/${this.getFileSize(totalFileSize)}`;
                    }
                }
                return 'Sending file to worker';
            } else if (wsData.processStage == ProcessStageTypes.PREPROCESS || wsData.processStage == ProcessStageTypes.PROCESSING) {
                return data;
            }
            if (wsData.processStage == ProcessStageTypes.COMPLETED && data.toLocaleLowerCase() === 'completed') {
                const startTimeInMilli = PSDate.from(fileUpload.uploadStartTime).getTime();
                if (fileUpload.processEndTime) {
                    const endTimeInMilli = PSDate.from(fileUpload.processEndTime).getTime();
                    const prettyTimeMs = new PSRedableDuration(PSDate.from(fileUpload.uploadStartTime))
                        .fromDate(PSDate.from(fileUpload.processEndTime));
                    return `${data}: Took ${prettyTimeMs}`;
                }
                const currentTimeInMilli = PSDate.from().toDate().getTime();
                const prettyTime = new PSRedableDuration(PSDate.from().subtract(currentTimeInMilli - startTimeInMilli, 'ms'))
                    .fromNow();
                return `${data} ${prettyTime}`;
            }
            return data || 'Server Processing File';
        } else if (dataType === 'ratio') {
            return 'Server Processing File';
        } else if (dataType === 'percentage') {
            const message = get(fileUpload, 'ws.readableProcessStage') || 'Server Processing File';
            return message;
        } else {
            return 'Waiting for server to reply';
        }
    }

    getProcessInfo(fileUpload: FileUpload): string {
        if (!this.fileProcessInfo) {
            this.fileProcessInfo = this._getProcessInfo(fileUpload);
        }
        return this.fileProcessInfo;
    }

    private _getProcessInfo(fileUpload: FileUpload): string {
        const inputExtension = getExtension(fileUpload.name);
        if (fileUpload.processType === ProcessTypes.SPLIT) {
            const splitOptions = ConvertOptionsUtils.getPropertyValue(fileUpload, 'split');
            if (splitOptions && splitOptions.formValue === 'page_range') {
                return `Extracted PDF ${splitOptions.formChildren[0].formValue} to ${splitOptions.formChildren[1].formValue}`;
            }
            if (fileUpload.fromType.includes('archive')) {
                return `Extracted to individual files`;
            }
            return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}ted PDF to individual pages`;
        } else if (fileUpload.processType === ProcessTypes.COMPRESS) {
            const outputQualityValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'outputQuality')
                || ConvertOptionsUtils.getPropertyValue(fileUpload, 'outputQualityAndSize');

            const crfValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'crf')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'crf');
            const presetValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'preset')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'preset');
            const qminValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'qmin')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'qmin');
            const qmaxValue = ConvertOptionsUtils.getPropertyValue(fileUpload, 'qmax')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'qmax');

            const minrate = ConvertOptionsUtils.getPropertyValue(fileUpload, 'minrate')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'minrate');
            const maxrate = ConvertOptionsUtils.getPropertyValue(fileUpload, 'minrate')
                || ConvertOptionsUtils.getSubPropertyValue(outputQualityValue, 'minrate');
            if (outputQualityValue && isString(outputQualityValue)) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                            ${inputExtension.toLocaleUpperCase()} with quality
                            ${outputQualityValue === '-1' ? 'unchanged' : outputQualityValue + '%'}`;
            } else if (crfValue && presetValue) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                             ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and Preset ${presetValue}`;
            } else if (crfValue && qminValue && qmaxValue) {
                return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                            ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                            Quality ${qminValue}-${qmaxValue}%`;
            } else if (crfValue && minrate || minrate) {
                if (minrate &&
                    minrate) {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${minrate} (minrate) - ${maxrate} (maxrate)`;
                } else if (maxrate) {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${maxrate} (maxrate)`;
                } else {
                    return `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
                        ${inputExtension.toLocaleUpperCase()} with CRF ${crfValue} and
                        ${minrate} (minrate)`;
                }

            } else {
                const processInfo = `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))} ${inputExtension.toLocaleUpperCase()}`;
                return processInfo;
            }
        } else if (fileUpload.processType === ProcessTypes.MERGE) {
            return `${StringUtils.capitalizeFirstLetter(
                this.getProcessTypePastTense(fileUpload.processType))} ${fileUpload.fromType[0].toUpperCase()} files to ${fileUpload.toType.toUpperCase()}`;
        }
        else if (fileUpload.processType === ProcessTypes.EXTRACT) {
            return `${StringUtils.capitalizeFirstLetter(`Extracted ${fileUpload.extension[0].toUpperCase()}`)}`;
        }
        else if (fileUpload.processType === ProcessTypes.ARCHIVE) {
            return `${StringUtils.capitalizeFirstLetter(`Archived files to ${fileUpload.toType.toUpperCase()}`)}`;
        }

        const toExtensionOverride = ConvertOptionsV2Utils.getPropertyValue(fileUpload, 'toExtensionOverride');
        let toExtension;
        if (toExtensionOverride && toExtensionOverride !== 'SAME_AS_SOURCE') {
            toExtension = toExtensionOverride;
        } else if (toExtensionOverride === 'SAME_AS_SOURCE') {
            toExtension = inputExtension; // use input file's extension
        } else {
            toExtension = ConvertOptionsV2Utils.getPropertyValue(fileUpload, 'toExtension');
        }
        let extension = toExtension ? getExtension(toExtension) : undefined;
        try {
            if (extension === 'original') {
                extension = getExtension(fileUpload.originalInputFiles[0].originalName);
            }
        } catch (e) { }
        const processInfo = `${StringUtils.capitalizeFirstLetter(this.getProcessTypePastTense(fileUpload.processType))}
             ${inputExtension.toLocaleUpperCase()} to ${extension?.toLocaleUpperCase()}`;
        return processInfo;
    }

    getProcessTypePastTense(pType: string) {
        if (/resize$/i.test(pType)) {
            return 'resized';
        } else if (/merge$/i.test(pType)) {
            return 'merged';
        } else if (/extract$/i.test(pType)) {
            return 'extracted';
        } else if (/convert$/i.test(pType)) {
            return 'converted';
        } else if (/rotate$/i.test(pType)) {
            return 'rotated';
        } else if (/trim$/i.test(pType)) {
            return 'trimmed';
        } else if (/crop$/i.test(pType)) {
            return 'cropped';
        } else if (/compress$/i.test(pType)) {
            return 'compressed';
        } else {
            return pType;
        }
    }

    decodeData(fileUpload: FileUpload) {
        const wsData: WebsocketResponse = fileUpload.ws;
        if (wsData.dataType === 'percentage') {
            if (wsData.processStage === 'processing') {
                if (wsData.data.match(/^completed$/i)) {
                    return '100';
                }
                const val = parseFloat(wsData.data).toFixed(2);
                return val;
            }
        } else if (wsData.dataType === 'notification') {
            if (wsData.processStage === 'upload') {
                if (wsData.processStageSubState.match(/^completed$/i)) {
                    return '100';
                }
                const val = Number(100 * parseFloat(wsData.data) / fileUpload.size).toFixed(2);
                return val;
            }
            return wsData.data;
        } else if (wsData.dataType === 'ratio') {
            return wsData.data;
        } else {
            return 'Waiting for server to reply';
        }
    }

    isFileReady(fileInfo: FileUpload) {
        if (!fileInfo.result || Object.keys(fileInfo.result).length === 0) {
            return false;
        }
        return true;
    }

    checkStatus(fileInfo: FileUpload) {
        return this.processCompleteCheckService.requestToSendFileCompleteDataThroughWS(
            fileInfo.endpointData.processorMetadataList[0].processDomain,
            fileInfo.id,
        ).then(data => this.logger.trace7(data))
            .catch(err => this.logger.error({ error: err, message: `Error while checking status ${err.message}` }));
    }

    async checkStatusAndShowMessage(fileInfo: FileUpload) {
        try {
            await this.initializeScriptsAndWebsockets();
            this.processCompleteCheckService.requestToSendFileCompleteDataThroughWS(
                fileInfo.endpointData.processorMetadataList[0].processDomain,
                fileInfo.id,
            ).then(stateRespose => {
                if (stateRespose.state === 'success') {
                    this.uiContext.successToast(
                        'Process Success',
                        'Process successfully completed. Please refresh page.',
                    );
                } else if (stateRespose.state === 'error') {
                    this.uiContext.errorToast(
                        'Error',
                        'Process failed.',
                        false,
                        false,
                    );
                } else {
                    this.uiContext.warnToast(
                        'Unknown',
                        'It\'s looks like server still processing file.',
                        false,
                        false,
                    );
                }
            }).catch(err => {
                this.uiContext.handleError(err);
            }).finally(() => {
                this.forceUpdate();
            });
        } catch (e) {
            this.uiContext.handleError(e);
        }
    }

    getFileDownloadBtnText(fileUpload: FileUpload) {
        const numberOfOutputFiles = get(fileUpload, 'result.outputFiles', []).length;
        if (numberOfOutputFiles > 1) {
            return `Show Downloads (${numberOfOutputFiles} files)`
        }
        return 'Download';
    }

    getOutputFileNameIfAvailable(fileUpload: FileUpload) {
        let filename = fileUpload.name;
        const override = fileUpload.options?.toExtensionOverride;
        let toExtension;
        if (override && override !== 'SAME_AS_SOURCE') {
            toExtension = override;
        } else if (override === 'SAME_AS_SOURCE') {
            toExtension = getExtension(fileUpload.name);
        } else {
            toExtension = fileUpload.options?.toExtension ?? getExtension(fileUpload.originalInputFiles[0].originalName);
            if (toExtension === 'SAME_AS_SOURCE') {
                toExtension = getExtension(fileUpload.name);
            }
        }
        filename = replaceExtension(fileUpload.name, toExtension);
        return filename;
    }

    async removeFile(fileUpload: FileUpload) {
        await this.initializeScriptsAndWebsockets();
        this.uiContext.showConfirmationWarningDialog(
            'Delete File',
            'Are you sure you want to delete?',
            async () => {
                await this.fileManagerService.removeFile(fileUpload);
            },
        );
    }

    /**
     * Show the user their result.
     *
     * Two sources are always in play: the real file, and a server-rendered still of it. The
     * capability check only decides which to *start* with — no table is right on every browser,
     * OS and codec combination, so the modal attempts the real file and falls back to the still
     * on a genuine decode failure. Being wrong optimistically costs one request; being wrong
     * pessimistically costs the real preview. Both recover, and neither dead-ends the user,
     * which the old "download the file to view it" toast did on the exact control they clicked
     * to find out whether the conversion worked.
     */
    openPreview(fileUpload: FileUpload) {
        const kind = previewKindFor(fileUpload.toType);
        if (!kind) return;
        if (!this.isFileReady(fileUpload)) {
            this.uiContext.errorToast('Not ready yet', 'This file is still converting.', false, false);
            return;
        }
        canPreviewOutput(fileUpload.toType, (fileUpload as any).result?.videoCodec)
            .then(verdict => this.showPreview(fileUpload, kind, verdict))
            // An unreachable capability check is not a reason to refuse: attempt it.
            .catch(() => this.showPreview(fileUpload, kind, 'unknown'));
    }

    private showPreview(fileUpload: FileUpload, kind: PreviewKind, verdict: CapabilityResult) {
        // Audio has no server-rendered still to fall back to (the thumb endpoint 404s),
        // so never offer one: a failed audio preview should say so, not chase a poster.
        const poster = kind === 'audio' ? null : this.outputPosterUrl(fileUpload);
        const format = (fileUpload.toType || 'this format').toUpperCase();
        const fallbackNotice = `Your browser can't open ${format} directly, so this is a still `
            + 'image of the converted result. Download the file to use it.';

        const realUrl = this.getRangeSupportedDirectDownloadLink(fileUpload, true);

        // 'no' means this browser definitely cannot decode it, so go straight to the poster
        // rather than spending the user's bandwidth on bytes that will not render. Download
        // still points at the real file.
        if (verdict === 'no' && poster) {
            MediaPreviewModalComponent.show({
                url: poster,
                downloadUrl: realUrl,
                mediaType: 'image' as const,
                filename: fileUpload.name,
                notice: fallbackNotice,
            });
            return;
        }

        // 'yes' and 'unknown' both attempt the real file; the modal swaps to the poster if it
        // genuinely fails to render, which is what makes a wrong capability answer survivable.
        MediaPreviewModalComponent.show({
            url: realUrl,
            downloadUrl: realUrl,
            mediaType: kind === 'video' ? 'video' as const
                : kind === 'audio' ? 'audio' as const
                    : fileUpload.toType === 'pdf' ? 'pdf' as const
                        : 'image' as const,
            filename: fileUpload.name,
            fallbackUrl: poster || undefined,
            fallbackNotice: poster ? fallbackNotice : undefined,
        });
    }

    /** Server-rendered still of the output, or null when there is nothing to ask for. */
    private outputPosterUrl(fileUpload: FileUpload): string | null {
        const output = fileUpload?.result?.output;
        if (!output || Array.isArray(output)) return null;
        const tokens = output.split('\\');
        return this.fileDownloadService.getOutputThumbnailLink(fileUpload, tokens[tokens.length - 1]);
    }

    async saveToDropbox(filename: string, downloadLink: string) {
        const instant = await this.spinner.show('Uploading to Dropbox');
        var options = {
            files: [
                // You can specify up to 100 files.
                { 'url': downloadLink, 'filename': filename },
                // ...
            ],

            // Success is called once all files have been successfully added to the user's
            // Dropbox, although they may not have synced to the user's devices yet.
            success: () => {
                // Indicate to the user that the files have been saved.
                this.spinner.hide(instant);
            },

            // Progress is called periodically to update the application on the progress
            // of the user's downloads. The value passed to this callback is a float
            // between 0 and 1. The progress callback is guaranteed to be called at least
            // once with the value 1.
            progress: (progress) => {
                this.spinner.updateMessage(instant, `Uploading to Dropbox. ${progress * 100} completed.`);
                if (progress === 1) {
                    this.spinner.hide(instant);
                }
            },

            // Cancel is called if the user presses the Cancel button or closes the Saver.
            cancel: () => {
                this.spinner.hide(instant);
            },

            // Error is called in the event of an unexpected response from the server
            // hosting the files, such as not being able to find a file. This callback is
            // also called if there is an error on Dropbox or if the user is over quota.
            error: (errorMessage) => {
                this.spinner.hide(instant);
                this.uiContext.showErrorDialog({ title: 'Error', text: errorMessage });
            }
        };
        Dropbox.save(downloadLink, filename, options);
    }

    getFooterButtons(fileUpload) {
        return <div className={styles['footer-buttons']} key={'file-list-item-' + fileUpload.id} data-jkey={`file-list-item-footer`}>
            <div className={styles['action-btns']}>
                {
                    this.zipSupported && <button
                        disabled={this.state.zipDownloadInProgress || !this.isFileReady(fileUpload) || null}
                        data-url={this.getZipFileURL(fileUpload)}
                        className={`${styles['btn']} ${!fileUpload.result || (fileUpload.result && !fileUpload.result.zip) ? 'hide' : ''}`}
                        onClick={() => this.onZipDownloadBtnHandler(fileUpload)}
                        data-jkey={`download-btn-zip`}>
                        Download All (Zip)
                    </button>
                }

                <button data-url={this.getDownloadURL(fileUpload)}
                    className={`${styles['btn']} ${styles['btn-primary']}`}
                    onClick={() => this.onDownloadBtnHandler(fileUpload)}
                    disabled={!this.isFileReady(fileUpload)}
                    data-jkey={`download-btn`}>
                    {this.getFileDownloadBtnText(fileUpload)}
                </button>

                {
                    /* Labelled, and next to Download rather than buried at the end of an
                       unlabelled icon row. On lossy image/GIF output a quarter of users
                       download nothing (see RETENTION_FIXES.md T2.4) -- they cannot judge
                       quality they were never shown. */
                    previewKindFor(fileUpload.toType) && <button
                        className={styles['btn']}
                        disabled={!this.isFileReady(fileUpload)}
                        onClick={() => this.openPreview(fileUpload)}
                        data-jkey={`preview-btn`}>
                        <img src="/icons/svg/play.svg" alt="" aria-hidden="true" /> Preview
                    </button>
                }

                {
                    false && this.hasDirectDownloadLink(fileUpload) && <div style={{ display: 'inline-block' }}>
                        <div className="g-savetodrive"
                            data-src={this.getRangeSupportedDirectDownloadLink(fileUpload)}
                            data-filename={fileUpload.originalInputFiles[0].originalName}
                            data-sitename="www.xconvert.com"
                            data-jkey={`download-drive`}>
                        </div>
                    </div>
                }

                {
                    this.hasDirectDownloadLink(fileUpload) && <Tooltip text="Save To Dropbox">
                        <button
                            className={`${styles['btn']} ${styles['btn-icon']}`}
                            disabled={!this.isFileReady(fileUpload)}
                            onClick={() => this.saveToDropbox(fileUpload.originalInputFiles[0].originalName, this.getRangeSupportedDirectDownloadLink(fileUpload))} >
                            <img className={styles['dropdox-image']} src="/img/dropbox.png" alt=""
                                data-jkey={`download-dropbox`} />
                        </button>
                    </Tooltip>
                }

                <Tooltip text="Downloadable Link">
                    <button data-url={this.getDownloadURL(fileUpload)}
                        className={`${styles['btn']} ${styles['btn-icon']}`}
                        disabled={!this.isFileReady(fileUpload)}
                        onClick={() => this.onDirectDownloadBtnHandler(fileUpload)}
                        data-jkey={`file-list-item`}>
                        <img src="/icons/svg/link-45deg.svg" alt="" aria-hidden="true" />
                    </button>
                </Tooltip>

                {
                    this.statusCheckButtonEnabled && <Tooltip text="Check Status">
                        <button
                            className={`${styles['btn']} ${styles['btn-icon']}`}
                            onClick={() => this.checkStatusAndShowMessage(fileUpload)}
                            data-jkey={`check-status`}>
                            <img src="/icons/svg/question.svg" alt="" aria-hidden="true" />
                        </button>
                    </Tooltip>
                }
                {
                    this.reeditButtonEnabled && <Tooltip text="Re-edit">
                        <button className={styles['btn']}
                            onClick={() => this.reedit(fileUpload)}
                            data-jkey={`edit-btn`}>
                            <img src="/icons/svg/pencil-square.svg" alt="" aria-hidden="true" /> Re-edit
                        </button>
                    </Tooltip>
                }
                {
                    this.deleteButtonEnabled && <Tooltip text="Delete">
                        <button className={`${styles['btn']} ${styles['btn-danger']} ${styles['btn-icon']}`}
                            onClick={() => this.removeFile(fileUpload)}
                            data-jkey={`delete-btn`}>
                            <img src="/icons/svg/trash.svg" alt="" aria-hidden="true" />
                        </button>
                    </Tooltip>
                }

            </div>
            {this.getNextSteps(fileUpload)}
        </div>
    }

    /**
     * "What next?" for this specific file, on this specific card.
     *
     * It used to be one block at the bottom of /downloads derived from the newest result only.
     * With three finished files that is ambiguous -- which one does it act on? -- and measured on
     * QA at 412px it rendered at y=2,420 against a first card at y=501, so nobody reached it.
     * A continuation belongs beside the Download button for the file it continues.
     */
    /**
     * Has this file's conversion actually failed?
     *
     * `status === FILEUPLOAD_STATE_FAILED` only covers a failed UPLOAD, so it is false for a
     * file that uploaded fine and then failed to convert -- which is most failures. Anything
     * deciding whether to show success-shaped UI needs all three signals, not just that one.
     */
    hasFailed(fileUpload: FileUpload): boolean {
        return fileUpload?.status === this.FILEUPLOAD_STATE_FAILED
            || fileUpload?.result?.error === true
            || fileUpload?.ws?.processStageSubState === ProcessStageSubStateTypes.FAILED;
    }

    getNextSteps(fileUpload: FileUpload) {
        if (!this.isFileReady(fileUpload) || fileUpload?.result?.error === true) return null;
        const steps = suggestionsFor(fileUpload.toType, (fileUpload as any)?.pageSlug || '', 3)
            .filter(t => !t.requiresBrowserDecode || this.editorSafe === true)
            .slice(0, 2);
        if (!steps.length) return null;

        return <div className={styles['next-steps']} data-jkey="card-next-steps">
            <span className={styles['next-steps-label']}>Next:</span>
            {steps.map((t, i) => (
                <a key={t.slug} href={`/${t.slug}`}
                    className={styles['next-step-chip']}
                    aria-busy={this.chaining === t.slug}
                    onClick={(e) => this.chainClick(e, fileUpload, t.slug, i)}>
                    {this.chaining === t.slug ? 'Taking your file…' : t.label}
                </a>
            ))}
            {this.chainNotice && (
                <span role="status" className={styles['next-steps-notice']}>{this.chainNotice}</span>
            )}
        </div>;
    }

    /**
     * Carry this card's output to the next tool. Modified clicks are left alone -- those are the
     * user asking for a new tab -- and every failure still navigates, after saying why.
     */
    private chainClick = async (e: any, fileUpload: FileUpload, slug: string, rank: number) => {
        if (e?.metaKey || e?.ctrlKey || e?.shiftKey || e?.button === 1) return;
        e.preventDefault();
        if (this.chaining) return;
        this.chaining = slug;
        this.chainNotice = null;
        this.forceUpdate();

        try {
            container.get(MetricsService).emit(MetricEventName.NEXT_TOOL_CLICKED, { toSlug: slug, rank });
        } catch { /* never load-bearing */ }

        let downloadUrl: string | null = null;
        try {
            const output = fileUpload?.result?.output;
            // Multi-output results have no single file to carry; the chip still navigates.
            if (output && !Array.isArray(output)) {
                downloadUrl = this.getRangeSupportedDirectDownloadLink(fileUpload, true);
            }
        } catch { /* fall through to an uncarried navigation */ }

        const result = await chainToTool(fileUpload, slug, downloadUrl);
        try {
            container.get(MetricsService).emit(MetricEventName.NEXT_TOOL_CHAINED, {
                toSlug: slug, carried: result.carried, reason: result.reason || '',
            });
        } catch { /* never load-bearing */ }

        if (!result.carried) {
            this.chaining = null;
            this.chainNotice = result.reason === 'too-large'
                ? 'Too large to carry over — pick it again on the next page.'
                : 'Couldn\'t bring the file across — pick it again on the next page.';
            this.forceUpdate();
            await new Promise(r => setTimeout(r, 1800));
        }
        window.location.assign(result.href);
    };

    render() {
        const shouldShowProgressbar = this.props.fileUpload.status !== this.FILEUPLOAD_STATE_FAILED && this.props.fileUpload.ws
            && ((this.props.fileUpload.ws.processStage === 'processing' && this.props.fileUpload.ws.dataType === 'percentage')
                || (this.props.fileUpload.ws.processStage === 'upload' && this.props.fileUpload.ws.dataType === 'notification'));
        return <div className={`id${this.props.fileUpload.id} ${styles['card']}`}>
            <div className={styles['card-body']} data-jkey={`file-list-item-card`}>

                <div className={`${styles['card-title']}`} data-jkey={`file-list-item-title`}>
                    {this.getOutputFileNameIfAvailable(this.props.fileUpload)}
                </div>
                <div className={styles['card-content']} >
                    {
                        !this.props.fileUpload.readableMessage && !this.props.fileUpload.result && (!this.props.fileUpload.ws || !this.props.fileUpload.ws.processStage)
                        && <div className={styles['server-reply']}>
                            <div className={styles['loader']}>
                                <RippleLoaderComponent size={13}></RippleLoaderComponent>
                            </div> Process starting...
                        </div>
                    }
                    {
                        this.fileUpload.readableMessage
                        && (!this.fileUpload.ws || (this.fileUpload.ws && this.fileUpload.ws.processStage !== 'processing' && this.fileUpload.ws.dataType !== 'percentage'))
                        && <div className={styles['server-reply']}>
                            {
                                this.fileUpload.status !== this.FILEUPLOAD_STATE_FAILED
                                && (!this.fileUpload.result || (this.fileUpload.result && !this.fileUpload.result.output))
                                && <div className={styles['loader']}>
                                    <RippleLoaderComponent size={13}></RippleLoaderComponent>
                                </div>
                            }
                            {this.props.fileUpload.readableMessage}
                        </div>
                    }

                    {
                        this.props.fileUpload.status !== this.FILEUPLOAD_STATE_FAILED && !this.props.fileUpload.readableMessage && this.props.fileUpload.ws
                        // A failed conversion is reported by the conversion-error block below.
                        // Without this guard both render the same text -- the status line in
                        // grey, the error line in red, one directly above the other.
                        && this.props.fileUpload.ws.processStageSubState !== ProcessStageSubStateTypes.FAILED
                        && <div className={`${styles['completed']} ${styles['flashit']}`}>
                            {
                                !this.props.fileUpload.result || (this.props.fileUpload.result && !this.props.fileUpload.result.output) && <div className="loader">
                                    <RippleLoaderComponent size={13}></RippleLoaderComponent>
                                </div>
                            }
                            {this.decodeDataType(this.props.fileUpload, this.props.fileUpload.ws.dataType, this.props.fileUpload.ws.data)}
                        </div>
                    }

                    {
                        this.props.fileUpload.ws
                        && this.props.fileUpload.ws.processStageSubState === ProcessStageSubStateTypes.FAILED
                        && this.props.fileUpload.ws.dataType !== 'enforcement_error'
                        && <div className={styles['conversion-error']}>
                            {this.props.fileUpload.ws.data || 'Conversion failed.'}
                        </div>
                    }

                    {
                        this.props.fileUpload.ws
                        && this.props.fileUpload.ws.processStageSubState === ProcessStageSubStateTypes.FAILED
                        && this.props.fileUpload.ws.dataType === 'enforcement_error'
                        && <div className={styles['conversion-error']}>
                            {this.decodeDataType(this.props.fileUpload, 'enforcement_error', this.props.fileUpload.ws.data)}
                            {(() => { try { const d = JSON.parse(this.props.fileUpload.ws.data); return d.upgradeRequired ? <span> — <a href="/pricing" style={{color: '#007bff', textDecoration: 'underline'}}>Upgrade your plan</a></span> : <span> — Try again later.</span>; } catch { return null; } })()}
                        </div>
                    }

                    {
                        this.props.fileUpload.status === this.FILEUPLOAD_STATE_FAILED
                        && <div>
                            Upload failed...
                        </div>
                    }

                    {
                        this.props.fileUpload.result
                        && this.props.fileUpload.result.sizes
                        && this.props.fileUpload.result.sizes.outputs
                        && <div>
                            {this.getProcessInfo(this.props.fileUpload)}
                        </div>
                    }

                    {
                        this.props.fileUpload.result
                        && this.props.fileUpload.result.sizes
                        && this.props.fileUpload.result.sizes.outputs
                        && this.isSingleFile(this.props.fileUpload.result.sizes.outputs)
                        && <div>
                            {this.getOutputInfo(this.props.fileUpload)}
                        </div>
                    }

                    {
                        this.hasZipFileInfo
                        && <div>
                            {this.hasZipFileData}
                        </div>
                    }

                    {
                        this.props.fileUpload.result
                        && this.props.fileUpload.result.processingNotes
                        && this.props.fileUpload.result.processingNotes.length > 0
                        && <div className={styles['processing-notes']}>
                            {/* Every note renders. The old single-line + hover-tooltip form hid all
                                but the first note — including the best-effort disclosure ("Reaching
                                X would destroy this GIF…"), the one line the pre-convert copy
                                explicitly promises ("we'll tell you if that happens"). A tooltip is
                                not telling: mobile has no hover and desktop users don't know to. */}
                            {this.props.fileUpload.result.processingNotes.map((note, i) => (
                                <div key={i} className={styles['processing-notes-line']}>ⓘ {note}</div>
                            ))}
                        </div>
                    }

                    {

                        shouldShowProgressbar && <ProgressComponent
                            value={Number(this.decodeData(this.props.fileUpload))}
                            postFix='%'
                        >
                        </ProgressComponent>
                    }
                </div>
                {
                    !this.props.hideFooterButtons && this.getFooterButtons(this.props.fileUpload)
                }

            </div>
            {!this.hasFailed(this.props.fileUpload) &&
            <div className={styles['social-share-promote']}>
                {this.getShareCTA()}
                <div className={styles['social-share']}>
                    <div className={`${styles['share-btn']} ${styles['share-btn-primary']}`}
                        onClick={() => this.copyLink()}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/><path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z"/></svg>
                        {this.state.linkCopied ? 'Copied!' : 'Copy Link'}
                    </div>
                    <div className={styles['share-btn']}
                        onClick={() => this.shareOnWhatsApp()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp
                    </div>
                    <div className={styles['share-btn']}
                        onClick={() => this.openTwitter()}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.738 2h1.994L9.377 7.083 14.5 14h-4.011L7.346 9.805 3.751 14H1.757l4.658-5.437L1.5 2h4.113l2.84 3.834L11.738 2Zm-.7 10.781h1.105l-7.13-9.627H3.828l7.21 9.627Z" fill="#161616"/></svg>
                    </div>
                    <div className={styles['share-btn']}
                        onClick={() => this.shareOnFacebook()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#1877F2" xmlns="http://www.w3.org/2000/svg"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </div>
                    <div className={styles['share-btn']}
                        onClick={() => this.shareOnLinkedIn()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#0A66C2" xmlns="http://www.w3.org/2000/svg"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </div>
                </div>
            </div>}
        </div>
    }


    getShareCTA(): string {
        const fu = this.props.fileUpload;
        if (fu.processType === ProcessTypes.COMPRESS && fu.result?.sizes?.outputs) {
            try {
                const key = Object.keys(fu.result.sizes.outputs)[0];
                const outputSize = parseFloat(fu.result.sizes.outputs[key]);
                const reduction = Math.round(100 * (1 - outputSize / fu.size));
                if (reduction > 0) return `Compressed by ${reduction}%! Share this tool`;
            } catch {}
        }
        if (fu.processType === ProcessTypes.MERGE) return `Merged your files? Share this with your team`;
        return `This tool saved you time — share it with others`;
    }

    copyLink() {
        const url = `https://www.xconvert.com/${this.props.fileUpload.pageData?.uniqueProcessType || ''}`;
        navigator.clipboard.writeText(url).then(() => {
            this.setState({ linkCopied: true });
            setTimeout(() => this.setState({ linkCopied: false }), 2000);
        });
    }

    shareOnWhatsApp() {
        const url = `https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}`;
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(this.props.fileUpload.pageData.title + ' ' + url)}`, 'popupwindow', 'scrollbars=yes,width=700,height=400')?.focus();
    }

    shareOnFacebook() {
        var popUp = window.open(
            `https://www.facebook.com/sharer/sharer.php?u=https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}`,
            'popupwindow',
            'scrollbars=yes,width=700,height=400'
        );
        popUp.focus();
        return false;
    }

    openTwitter() {
        var popUp = window.open(`https://twitter.com/intent/tweet?url=https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}&text=${encodeURIComponent(this.props.fileUpload.pageData.title)}&via=xconvert_com&hashtags=xconvert`,
            'popupwindow',
            'scrollbars=yes,width=700,height=400'
        );
        popUp.focus();
        return false
    }

    shareOnLinkedIn() {
        var popUp = window.open(
            `https://www.linkedin.com/shareArticle?url=https://www.xconvert.com/${this.props.fileUpload.pageData.uniqueProcessType}`,
            'popupwindow',
            'scrollbars=yes,width=700,height=400'
        );
        popUp.focus();
        return false
    }
}
